import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetConsignmentLinesQuery,
  useGetPalletGroupQuery,
  useUpdatePalletGroup,
  addPallets,
  fromPalletGroup,
  lineTotals,
  palletizePortions,
  summarizePortions,
  type PalletPortion,
  type PalletTypeId
} from '@/entities/consignments';
import { getApiErrorMessage, useProjectSiteParams } from '@/shared/lib';

interface UseCreatePalletModalProps {
  consignmentId: string;
  /** Selected parts of lines to put on the new pallets */
  selectedPortions?: PalletPortion[];
  onClose: () => void;
}

export interface CreatePalletFormData {
  type: PalletTypeId;
  inventoryUnitsPerTransport: number;
  inventoryUnitType: string;
  transportUnitQuantity: number;
}

const NO_PORTIONS: PalletPortion[] = [];

/**
 * The create pallet dialog. With no lines selected it adds one empty pallet;
 * otherwise the selected parts are spread over the new pallets, like the legacy
 * "palletize" action. The dialog closes once the pallets are saved.
 */
export function useCreatePalletModal({
  consignmentId,
  selectedPortions = NO_PORTIONS,
  onClose
}: UseCreatePalletModalProps) {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  // State
  const [error, setError] = useState<string>('');

  const onSaveError = useCallback(
    (err: unknown) => setError(getApiErrorMessage(err, t('pallet.create.error.failed'))),
    [t]
  );
  const { savePalletConfig, isSaving } = useUpdatePalletGroup({ onError: onSaveError });

  const params = { consignmentId, projectId, siteId };
  const skip = !consignmentId || !projectId || !siteId;
  const { data: linesData, isLoading: isLinesLoading } = useGetConsignmentLinesQuery(params, { skip });
  const { data: palletGroup, isLoading: isGroupLoading } = useGetPalletGroupQuery(params, { skip });

  const lines = useMemo(() => linesData?.items ?? [], [linesData]);
  const config = useMemo(() => fromPalletGroup(palletGroup), [palletGroup]);

  const selectedLinesInfo = useMemo(
    () => summarizePortions(selectedPortions, config, lines),
    [selectedPortions, config, lines]
  );

  // Handle modal close
  const handleClose = useCallback(() => {
    setError('');
    onClose();
  }, [onClose]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (formData: CreatePalletFormData) => {
      setError('');
      const next =
        selectedPortions.length === 0
          ? addPallets(config, formData.type, 1).config
          : palletizePortions(
              config,
              selectedPortions,
              formData.type,
              formData.transportUnitQuantity,
              formData.inventoryUnitsPerTransport,
              lineTotals(lines)
            ).config;

      if (await savePalletConfig({ consignmentId, projectId, siteId }, next)) handleClose();
    },
    [lines, config, selectedPortions, consignmentId, projectId, siteId, savePalletConfig, handleClose]
  );

  return {
    // Data
    lines,
    selectedLinesInfo,

    // State
    isLoading: isLinesLoading || isGroupLoading,
    isProcessing: isSaving,
    error,

    // Actions
    handleSubmit,
    handleClose
  };
}
